import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { addAssociationMutation, updateAssociationMutation, getAssociationsInZoneQuery } from '../../queries/queries';
import { Form, Group, Field } from '../../common/index';
import Select from 'react-select';
import { inviteAssociationAdmin } from '../../queries/invitations';
import { useMutation } from '@apollo/client';
import FieldWrapper from '../../common/FieldWrapper';
import { IoInformationCircle } from 'react-icons/io5';
import Tooltip from '../../common/Tooltip';
function AssociationsForm(props) {
  const { content, onComplete, associations } = props;
  const [sContent, setSContent] = useState(content);

  const [invitationEmails, setInvitationEmails] = useState([]);
  const [invitationAssociationAdmin] = useMutation(inviteAssociationAdmin, {
    onCompleted: (res) => {
      console.log('Inbjudan skickad till föreningsansvarig', res);
    },
    onError: (err) => console.log(err)
  });

  useEffect(() => {
    setSContent({
      ...content,
      bStreetAddress: content.billingAddress && content.billingAddress.streetAddress,
      bZipCode: content.billingAddress && content.billingAddress.zipCode,
      bCity: content.billingAddress && content.billingAddress.city,
      bState: content.billingAddress && content.billingAddress.state,
      // Load bank information from bankInformation if available
      clearingNumber: content.bankInformation && content.bankInformation.clearingNumber,
      accountNumber: content.bankInformation && content.bankInformation.accountNumber,
      accountOwner: content.bankInformation && content.bankInformation.accountOwner,
      // Load resource bank info from resourceBank if available
      resourceBankUsername: content.resourceBank && content.resourceBank.username,
      resourceBankPassword: content.resourceBank && content.resourceBank.password,
      // Load demo booklet info from demoBooklet if available
      demoBookletEmail: content.demoBooklet && content.demoBooklet.email,
      demoBookletPassword: content.demoBooklet && content.demoBooklet.password,
      // Set default to current domain (if new association)
      shopShareDomain: content?._id
        ? content.shopShareDomain
        : window.location.hostname,
      visibleOnStartpageOfDomains: content?._id
        ? content.visibleOnStartpageOfDomains
        : [],
    });
  }, [content]);

  const ss = (c) => setSContent({ ...sContent, ...c });

  return (
    <tr key={content ? content._id : 'new'} className="open">
      <td colSpan="100">
        <Form
          sContent={{
            ...sContent,
            // Transform the bank fields into bankInformation object
            bankInformation: sContent.clearingNumber || sContent.accountNumber || sContent.accountOwner ? {
              clearingNumber: sContent.clearingNumber || null,
              accountNumber: sContent.accountNumber || null,
              accountOwner: sContent.accountOwner || null,
            } : null,
            // Remove the individual fields so they don't get sent to the mutation
            clearingNumber: undefined,
            accountNumber: undefined,
            accountOwner: undefined,
            // Transform resource bank fields into resourceBank object
            resourceBank: sContent.resourceBankUsername || sContent.resourceBankPassword ? {
              username: sContent.resourceBankUsername || null,
              password: sContent.resourceBankPassword || null,
            } : null,
            resourceBankUsername: undefined,
            resourceBankPassword: undefined,
            // Transform demo booklet fields into demoBooklet object
            demoBooklet: sContent.demoBookletEmail || sContent.demoBookletPassword ? {
              email: sContent.demoBookletEmail || null,
              password: sContent.demoBookletPassword || null,
            } : null,
            demoBookletEmail: undefined,
            demoBookletPassword: undefined,
          }}
          parent="zoneId"
          get={getAssociationsInZoneQuery}
          add={addAssociationMutation}
          update={updateAssociationMutation}
          onComplete={onComplete}
          invitation={invitationAssociationAdmin}
          invitationAdminEmails={invitationEmails}
        >
          <Group title="Allmänt">
            <Field type="text" req name="name" title="Förening" value={sContent.name} onChange={ss} />
            <Field
              type="selectStatic"
              name="type"
              title="Typ"
              value={sContent.type}
              onChange={ss}
              selectData={[
                { value: 'association', label: 'Förening' },
                { value: 'school', label: 'Skola' }
              ]}
            />
            <Field type="textarea" name="note" title="Anteckning" value={sContent.note} onChange={ss} />
            <Field type="image" name="logoURL" title="Logotyp (max 1MB)" value={sContent.logoURL} onChange={ss} />
            <Field
              type="image"
              name="shopShareImageURL"
              title="Delningsbild, shop (max 1MB)"
              value={sContent.shopShareImageURL}
              onChange={ss}
            />
          </Group>
          <Group title="Kontaktuppgifter">
            <Field type="phone" name="phone" title="Telefon" value={sContent.phone} onChange={ss} />
            <Field type="text" name="bStreetAddress" title="Gatuadress" value={sContent.bStreetAddress} onChange={ss} />
            <Field type="text" name="bZipCode" title="Postnummer" value={sContent.bZipCode} onChange={ss} />
            <Field type="text" name="bCity" title="Stad" value={sContent.bCity} onChange={ss} />
            <Field type="text" name="bState" title="Län" value={sContent.bState} onChange={ss} />
          </Group>
          <Group title="Betalningsuppgifter">
            <Field type="text" name="clearingNumber" title="Clearingnummer" value={sContent.clearingNumber} onChange={ss} />
            <Field type="text" name="accountNumber" title="Kontonummer" value={sContent.accountNumber} onChange={ss} />
            <Field type="text" name="accountOwner" title="Ägare av konto" value={sContent.accountOwner} onChange={ss} />
          </Group>
          <Group title="Försäljning">
            <FieldWrapper name="supportableAssociations" title="Valbara stödföreningar">
              <Select
                isMulti
                options={associations && associations
                  .filter((a) => a._id !== content?._id) // Exkludera nuvarande förening
                  .map((a) => ({ value: a._id, label: a.name }))}
                value={sContent.supportableAssociations && sContent.supportableAssociations.map((id) => {
                  const association = associations.find((a) => a._id === id);
                  return {
                    value: id,
                    label: association ? association.name : ''
                  };
                })}
                placeholder="Välj föreningar..."
                onChange={(selected) => ss({ supportableAssociations: selected?.map((s) => s.value) || [] })}
              />
            </FieldWrapper>
            <Field
              type="select"
              name="vatPercentage"
              title="Moms"
              value={sContent.vatPercentage}
              onChange={ss}
              selectData={[
                { _id: 0, name: '0% (momsfri)' },
                { _id: 25, name: '25%' }
              ]}
              selectDataInfo={{}}
              isDataLoading={false}
            />
            <Field
              type="date"
              name="salesStartDate"
              title="Första försäljningsdag"
              value={sContent.salesStartDate}
              onChange={ss}
            />
            <Field
              type="date"
              name="salesEndDate"
              title="Sista försäljningsdag"
              value={sContent.salesEndDate}
              onChange={ss}
            />
          </Group>
          <Group title="Inställningar">
            {/* 2026-03-26 Funktionen är föråldrad, ej fungerande och ej nyttjad sedan flera år.
            Funktionen ska, när den fungerar, skicka ett inbjudningsmail till angiven e-post där
            det finns en länk där personen får möjlighet att registrera ett konto med valfri epost
            som användarnamn. */}
            {/* <Field type="admins" name="invitationEmails" title="Bjud in för att bli Administratörer" value={invitationEmails} onChange={(value) => setInvitationEmails(value)} /> */}

            {/* Only allow adding admins on edit */}
            <Field
              type="admins"
              disabled={content._id === undefined ? true : false}
              name="adminEmails"
              title={
                <>
                  {'Adminstratörer '}
                  <Tooltip content="Det går endast att lägga till en adminstratör till föreningen ifall föreningen är angiven i häftet.">
                    <IoInformationCircle size={16} />
                  </Tooltip>
                </>
              }
              value={sContent.adminEmails} onChange={ss} />

            <Field
              type="text"
              name="shopShareDomain"
              title="Domän vid delning, avgör varumärke på vissa mail *"
              value={sContent.shopShareDomain}
              onChange={ss}
              req
            />
            {sContent._id && (
              <Field
                type="text"
                name="slug"
                title="URL-slug"
                value={sContent.slug}
                onChange={ss}
              />
            )}
            <Field
              x2={!!sContent._id}
              type="admins"
              name="visibleOnStartpageOfDomains"
              title={
                <>
                  Visas på startsidan på domäner
                  <Tooltip content="Avgör i vilka domäners listor (på startsidan) föreningens shop ska vara valbar. OBS! Föreningens shop går ändå att nå på samtliga domäner om man skriver hela URL:en.">
                    <IoInformationCircle size={16} />
                  </Tooltip>
                </>
              }
              placeholder='Ange en domän och tryck enter'
              value={sContent.visibleOnStartpageOfDomains}
              onChange={ss}
            />
            <Field
              x2
              type="selectStatic"
              name="subshopVisibilityInDashboard"
              title="Subshoppar i Mina sidor"
              value={sContent.subshopVisibilityInDashboard}
              onChange={ss}
              selectData={[
                { value: 'NONE', label: 'Visa inga subshoppar i Mina sidor' },
                { value: 'TEAMS', label: 'Visa lagshoppar i Mina sidor' },
                { value: 'TEAM_MEMBERS', label: 'Visa personliga webbshoppar i lagens Mina sidor' },
                { value: 'TEAMS_AND_MEMBERS', label: 'Visa lagshoppar och personliga webbshoppar i Mina sidor' }
              ]}
            />
          </Group>
          <Group title="Återförsäljarformulär">
            <Field
              type="text"
              name="resellerFormAssociations"
              title="Föreningar"
              value={sContent.resellerFormAssociations}
              onChange={ss}
            />
            <Field
              type="text"
              name="resellerFormTeams"
              title="Lag & klasser"
              value={sContent.resellerFormTeams}
              onChange={ss}
            />
          </Group>
          <Group title="Resursbanken">
            <Field type="text" name="resourceBankUsername" title="Användarnamn" value={sContent.resourceBankUsername} onChange={ss} />
            <Field type="text" name="resourceBankPassword" title="Lösenord" value={sContent.resourceBankPassword} onChange={ss} />
          </Group>
          <Group title="Demohäfte">
            <Field type="text" name="demoBookletEmail" title="E-post" value={sContent.demoBookletEmail} onChange={ss} />
            <Field type="text" name="demoBookletPassword" title="Lösenord" value={sContent.demoBookletPassword} onChange={ss} />
          </Group>
        </Form>
      </td>
    </tr>
  );
}

AssociationsForm.propTypes = {
  content: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
  associations: PropTypes.array,
};

export default AssociationsForm;
